/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { BackButton } from '../components/BackButton';
import { PageContainer } from '../components/layout/PageContainer';
import YesNoAction from '../components/YesNoAction';
import { TestIds } from '../testIds';
import { Quote } from '../features/quotes/storage/types';
import { useSelector } from 'react-redux';
import { selectContactInfo, selectUserContactDetails } from '../features/azure-b2c/storage';
import ContactCard from '../components/ContactCard';
import { selectStores } from '../features/stores/storage/stores.slice';
import { StoreInfo } from '../features/stores/storage/stores.types';

export const QuoteOrderSubmitted = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const confirmationId = params.confirmationId;
  const quotes: Quote | undefined = location.state?.quotes;
  const relationshipOwner = useSelector(selectContactInfo);
  const userDetails = useSelector(selectUserContactDetails);
  const stores = useSelector(selectStores);
  const [storeInfo, setStoreInfo] = React.useState<StoreInfo>();

  React.useEffect(() => {
    if (quotes?.delivery.pickupStoreId) {
      const store = stores.find((e) => e.inventoryLocationId === quotes.delivery.pickupStoreId);
      setStoreInfo(store);
    }
  }, [quotes, stores]);

  const cartOrMessage = React.useMemo(() => {
    const bodyForPickup = (
      <>
        <Typography variant='body2' sx={{ mb: 2 }}>
          Your order has been sent to Farmlands {storeInfo?.orgUnitName} for processing and the team
          will be in contact with you directly to let you know when it is ready to pick up.
        </Typography>
        <Typography variant='body2' sx={{ mb: 2 }}>
          You will receive an email confirmation of your order and you can also track this purchase
          in the Orders page.
        </Typography>
      </>
    );

    const bodyForDelivery = (
      <Typography variant='body2' sx={{ mb: 2 }}>
        You will receive an email confirmation that your order has been received. Before your order
        is processed, <b>we will reach out to confirm any additional delivery costs</b>, if
        applicable.
      </Typography>
    );

    return (
      <>
        <YesNoAction
          display='Order submitted'
          subheading={
            <>
              <Typography variant='h4' sx={{ mt: 4, mb: 2 }}>
                Thanks {userDetails?.firstName}!
              </Typography>
              <Typography
                variant='body2'
                fontWeight='bold'
                sx={{ mt: 2, mb: 2 }}
                data-testid={TestIds.feat.checkout.confirmationId}
              >
                Confirmation ID: {confirmationId}
              </Typography>
            </>
          }
          body={storeInfo ? bodyForPickup : bodyForDelivery}
        />

        {storeInfo ? (
          <ContactCard
            sx={{ mt: 4 }}
            title='If you have any questions please contact your chosen Farmlands store.'
            body={
              <Typography variant='body1' color='white' fontWeight={700} lineHeight={'27px'}>
                <span>{storeInfo.orgUnitName}</span>
                <br />
                <Typography variant='body1' color='white' component='span'>
                  {storeInfo.address}
                </Typography>
              </Typography>
            }
            email={storeInfo.email}
            phone={storeInfo.phone}
            titleGap={24}
            storeName={storeInfo.orgUnitName}
            data-testid={TestIds.components.contactCard.card}
          />
        ) : (
          <ContactCard
            sx={{ mt: 4 }}
            title='If you have any questions about your order please contact a Farmlands representative.'
            {...relationshipOwner}
            data-testid={TestIds.components.contactCard.card}
          />
        )}
      </>
    );
  }, [quotes, confirmationId, storeInfo]);

  return (
    <PageContainer data-testid={TestIds.pages.quoteOrderSubmitted}>
      <BackButton text='Quotes' onClick={() => navigate('/quotes')} />
      {cartOrMessage}
    </PageContainer>
  );
};
